import {
    formTypeToSchemaNode,
    PAYLOAD_FIELD_TYPE_OPTIONS,
    payloadFieldTypeLabel,
    schemaNodeToFormType,
} from "./fieldTypeRegistry";

/** Clés JSON Schema à ne jamais traiter comme champs métier. */
export const SCHEMA_META_KEYS = new Set(["type", "properties"]);

export {
    PAYLOAD_FIELD_TYPE_OPTIONS,
    payloadFieldTypeLabel,
    schemaNodeToFormType,
    formTypeToSchemaNode,
};

export function looksLikeJsonSchema(schema) {
    return Boolean(
        schema && typeof schema === "object" && !Array.isArray(schema)
        && schema.type === "object" && schema.properties && typeof schema.properties === "object",
    );
}

export function looksLikeManualMap(schema) {
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) return false;
    const keys = Object.keys(schema).filter((k) => !SCHEMA_META_KEYS.has(k));
    if (keys.length === 0) return false;
    return keys.every((k) => {
        const v = schema[k];
        return v && typeof v === "object" && !Array.isArray(v) && ("type" in v || "description" in v);
    });
}

export function filterSchemaMetaKeys(map) {
    const out = {};
    Object.entries(map || {}).forEach(([k, v]) => {
        if (!SCHEMA_META_KEYS.has(k)) out[k] = v;
    });
    return out;
}

/** Propriétés métier extraites du schéma (JSON Schema ou manual map). */
export function getPayloadSchemaProperties(schema) {
    if (!schema || typeof schema !== "object") return {};
    if (looksLikeJsonSchema(schema)) return schema.properties || {};
    if (looksLikeManualMap(schema)) return filterSchemaMetaKeys(schema);
    return filterSchemaMetaKeys(schema);
}

function mergeManualWithSchemaProperties(manual, schema) {
    const props = getPayloadSchemaProperties(schema);
    const out = {};
    Object.entries(manual || {}).forEach(([key, val]) => {
        const fromSchema = props[key];
        out[key] = fromSchema
            ? { ...fromSchema, ...val, format: val?.format || fromSchema?.format }
            : val;
    });
    return out;
}

/**
 * Champs payload pour l'éditeur / conditions.
 * Pour json_schema (ex. sale WooCommerce), payload_schema.properties est la source de vérité
 * (type + format email/tel/date-time). payload_fields_manual n'a que type:string sans format.
 */
export function extractPayloadFieldsManual(detail) {
    if (!detail || typeof detail !== "object") return {};

    const schema = detail.payload_schema;
    const schemaFormat = detail.payload_schema_format || (looksLikeJsonSchema(schema) ? "json_schema" : null);

    if (schemaFormat === "json_schema" || looksLikeJsonSchema(schema)) {
        const props = getPayloadSchemaProperties(schema);
        if (Object.keys(props).length > 0) return props;
    }

    if (detail.payload_fields_manual && typeof detail.payload_fields_manual === "object") {
        const manual = filterSchemaMetaKeys(detail.payload_fields_manual);
        return mergeManualWithSchemaProperties(manual, schema);
    }

    if (!schema || typeof schema !== "object") return {};

    if (looksLikeManualMap(schema)) return filterSchemaMetaKeys(schema);
    return filterSchemaMetaKeys(schema);
}

/** Corps payload_schema à envoyer au PATCH selon le format. */
export function buildPayloadSchemaForSave(schemaFormat, manualMap) {
    if (!manualMap || Object.keys(manualMap).length === 0) return null;
    if (schemaFormat === "json_schema") {
        return { type: "object", properties: manualMap };
    }
    return manualMap;
}

export function manualMapToFormFields(manualMap) {
    return Object.entries(manualMap || {}).map(([key, val]) => ({
        id: `${key}_${Math.random().toString(36).slice(2, 9)}`,
        key,
        type: schemaNodeToFormType(val || {}),
        description: val?.description || "",
    }));
}

export function formFieldsToManualMap(fields) {
    const out = {};
    (fields || []).forEach((f) => {
        const key = f.key?.trim();
        if (!key) return;
        out[key] = formTypeToSchemaNode(f.type, f.description);
    });
    return out;
}

export function hasPayloadFieldsManual(detail) {
    return Object.keys(extractPayloadFieldsManual(detail)).length > 0;
}

export function resolvePayloadSchemaFormat(detail) {
    if (detail?.payload_schema_format) return detail.payload_schema_format;
    const manual = extractPayloadFieldsManual(detail);
    if (Object.keys(manual).length === 0 && !detail?.payload_schema) return null;
    if (looksLikeJsonSchema(detail?.payload_schema)) return "json_schema";
    return "manual_map";
}
